import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService, AssemblyAdminService, PipelineAttemptsService } from 'app/API';
import { adminDashboardKeys } from 'app/queries/adminDashboard/adminDashboardKeys';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { createFetchCurrentAttemptsQueryFn } from '../../api/fetchCurrentAttempts';
import { useClampListPage } from 'app/hooks/useClampListPage';
import { createFetchDashboardInfoQueryFn } from '../../api/fetchDashboardInfo';
import { createFetchErroredJobsQueryFn } from '../../api/fetchErroredJobs';
import { createFetchEventDurationSummaryQueryFn } from '../../api/fetchEventDurationSummary';
import { createFetchJobSubmissionSummaryQueryFn } from '../../api/fetchJobSubmissionSummary';
import { createFetchProcessingJobsQueryFn } from '../../api/fetchProcessingJobs';
import { CurrentAttemptsCard } from './components/CurrentAttemptsCard';
import { ErroredJobsCard } from './components/ErroredJobsCard';
import { EventDurationCard } from './components/EventDurationCard';
import { JobSubmissionsCard } from './components/JobSubmissionsCard';
import { ProcessingJobsCard } from './components/ProcessingJobsCard';
import { QueueJobsSection } from './components/QueueJobsSection';
import { SummaryCards } from './components/SummaryCards';
import { UnhandledErrorsSection } from './components/UnhandledErrorsSection';
import type {
  ErroredPipelineJob,
  EventDurationStat,
  JobSubmissionBucket,
  PipelineAttempt,
  ProcessingPipelineJob,
} from './types/dashboardTypes';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

export const Content = () => {
  const { openModal, closeModal } = useModalContext();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [pipelineAttemptsPage, setPipelineAttemptsPage] = useState(1);
  const [pipelineAttemptsPageSize, setPipelineAttemptsPageSize] = useState(10);
  const [erroredJobsPage, setErroredJobsPage] = useState(1);
  const [erroredJobsPageSize, setErroredJobsPageSize] = useState(10);
  const [processingJobsPage, setProcessingJobsPage] = useState(1);
  const [processingJobsPageSize, setProcessingJobsPageSize] = useState(10);

  const infoQuery = useQuery({
    queryKey: adminDashboardKeys.info(),
    queryFn: createFetchDashboardInfoQueryFn(call),
  });
  const submissionSummaryQuery = useQuery({
    queryKey: adminDashboardKeys.submissionSummary(),
    queryFn: createFetchJobSubmissionSummaryQueryFn(call),
  });
  const eventDurationsQuery = useQuery({
    queryKey: adminDashboardKeys.eventDurations(),
    queryFn: createFetchEventDurationSummaryQueryFn(call),
  });
  const attemptsQuery = useQuery({
    queryKey: adminDashboardKeys.attemptList({ page: pipelineAttemptsPage, pageSize: pipelineAttemptsPageSize }),
    queryFn: createFetchCurrentAttemptsQueryFn(call),
    placeholderData: keepPreviousData,
  });
  const erroredJobsQuery = useQuery({
    queryKey: adminDashboardKeys.erroredJobList({ page: erroredJobsPage, pageSize: erroredJobsPageSize }),
    queryFn: createFetchErroredJobsQueryFn(call),
    placeholderData: keepPreviousData,
  });
  const processingJobsQuery = useQuery({
    queryKey: adminDashboardKeys.processingJobList({ page: processingJobsPage, pageSize: processingJobsPageSize }),
    queryFn: createFetchProcessingJobsQueryFn(call),
    placeholderData: keepPreviousData,
  });

  const dashboard = infoQuery.data;
  const submissionSummary = useMemo(
    () => (submissionSummaryQuery.data as JobSubmissionBucket[]) ?? [],
    [submissionSummaryQuery.data],
  );
  const eventDurationStats = useMemo(
    () => (eventDurationsQuery.data as EventDurationStat[]) ?? [],
    [eventDurationsQuery.data],
  );
  const pipelineAttempts = useMemo(() => (attemptsQuery.data?.data as PipelineAttempt[]) ?? [], [attemptsQuery.data]);
  const pipelineAttemptsCount = attemptsQuery.data?.count ?? 0;
  const erroredJobs = useMemo(
    () => (erroredJobsQuery.data?.data as ErroredPipelineJob[]) ?? [],
    [erroredJobsQuery.data],
  );
  const erroredJobsCount = erroredJobsQuery.data?.count ?? 0;
  const processingJobs = useMemo(
    () => (processingJobsQuery.data?.data as ProcessingPipelineJob[]) ?? [],
    [processingJobsQuery.data],
  );
  const processingJobsCount = processingJobsQuery.data?.count ?? 0;

  useClampListPage({
    page: pipelineAttemptsPage,
    setPage: setPipelineAttemptsPage,
    pageSize: pipelineAttemptsPageSize,
    count: attemptsQuery.data?.count,
    hasFreshData: attemptsQuery.isSuccess && !attemptsQuery.isPlaceholderData,
  });

  useClampListPage({
    page: erroredJobsPage,
    setPage: setErroredJobsPage,
    pageSize: erroredJobsPageSize,
    count: erroredJobsQuery.data?.count,
    hasFreshData: erroredJobsQuery.isSuccess && !erroredJobsQuery.isPlaceholderData,
  });

  useClampListPage({
    page: processingJobsPage,
    setPage: setProcessingJobsPage,
    pageSize: processingJobsPageSize,
    count: processingJobsQuery.data?.count,
    hasFreshData: processingJobsQuery.isSuccess && !processingJobsQuery.isPlaceholderData,
  });

  const reloadDashboard = () => {
    void queryClient.invalidateQueries({ queryKey: adminDashboardKeys.all });
  };

  const notifyMutation = useMutation({
    mutationFn: () => call(() => AdminService.notifyAboutNewVersion()),
  });

  const checkStuckWebhooksMutation = useMutation({
    mutationFn: () => call(() => AssemblyAdminService.checkStuckWebhooks()),
    onSuccess: (result) => {
      toast.success(
        `Stuck webhooks: ${result.checked} checked, ${result.requeued} re-enqueued, ${result.failed} failed`,
      );
      closeModal();
    },
    onError: () => toast.error('Unable to check stuck Assembly webhooks'),
  });

  const markAttemptFailedMutation = useMutation({
    mutationFn: (attemptId: string) => call(() => PipelineAttemptsService.markAttemptFailed({ attemptId })),
    onSuccess: () => {
      toast.success('Attempt marked as failed');
      reloadDashboard();
      closeModal();
    },
    onError: () => toast.error('Unable to update attempt status'),
  });

  const isLoading =
    infoQuery.isFetching ||
    submissionSummaryQuery.isFetching ||
    eventDurationsQuery.isFetching ||
    attemptsQuery.isFetching ||
    erroredJobsQuery.isFetching ||
    processingJobsQuery.isFetching ||
    checkStuckWebhooksMutation.isPending ||
    markAttemptFailedMutation.isPending;

  const sortedAttempts = useMemo(
    () =>
      [...pipelineAttempts].sort(
        (a: PipelineAttempt, b: PipelineAttempt) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
      ),
    [pipelineAttempts],
  );

  const submissionBuckets = useMemo(
    () =>
      [...submissionSummary].sort(
        (a: JobSubmissionBucket, b: JobSubmissionBucket) => new Date(a.hour).getTime() - new Date(b.hour).getTime(),
      ),
    [submissionSummary],
  );

  const sortedEventStats = useMemo(
    () => [...eventDurationStats].sort((a: EventDurationStat, b: EventDurationStat) => b.count - a.count),
    [eventDurationStats],
  );

  const sortedErroredJobs = useMemo(() => {
    return [...erroredJobs].sort(
      (a: ErroredPipelineJob, b: ErroredPipelineJob) => b.erroredDurationSeconds - a.erroredDurationSeconds,
    );
  }, [erroredJobs]);

  const sortedProcessingJobs = useMemo(() => {
    return [...processingJobs].sort(
      (a: ProcessingPipelineJob, b: ProcessingPipelineJob) => b.processingDurationSeconds - a.processingDurationSeconds,
    );
  }, [processingJobs]);

  const openNotifyModal = () => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: 'Confirm',
      description: 'Do you really want to notify all online users?',
      buttonText: 'Notify',
      onSuccess: () => {
        notifyMutation.mutate();
        closeModal();
      },
    });
  };

  const openCheckStuckAssemblyWebhooksModal = () => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: 'Check stuck Assembly webhooks',
      description:
        'Inspects Assembly webhooks queued for more than 3 hours and re-enqueues any whose transcripts have completed or errored. Runs hourly via cron — use this to trigger it on demand.',
      buttonText: 'Check now',
      onCancel: () => closeModal(),
      onSuccess: () => {
        checkStuckWebhooksMutation.mutate();
      },
    });
  };

  const confirmMarkAttemptFailed = (attemptId: string) => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: 'Mark attempt as failed',
      description: 'This will mark the current pipeline attempt as failed and mark the job as errored.',
      buttonText: 'Mark failed',
      onCancel: () => closeModal(),
      onSuccess: () => {
        markAttemptFailedMutation.mutate(attemptId);
      },
    });
  };

  const openUpdateErrorDescriptionModal = (id: string) => {
    openModal(ModalNamesEnum.UpdateClassifiedErrorDescription, {
      id,
      onCancel: () => closeModal(),
      onSuccess: () => reloadDashboard(),
    });
  };

  return (
    <main className="p-2 px-8 pb-[70px] min-h-[calc(100vh-120px)]">
      <LinearProgressLoader active={isLoading} />

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-[var(--color-text-muted)] text-lg">Pipeline health</div>
        <div className="flex gap-2">
          <button
            onClick={openCheckStuckAssemblyWebhooksModal}
            className="px-3 py-2 text-sm bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md text-[var(--color-text)] shadow-[0_4px_8px_var(--color-shadow)] hover:bg-[var(--color-surface-muted)]"
          >
            Check stuck Assembly webhooks
          </button>
          <button
            onClick={reloadDashboard}
            className="px-3 py-2 text-sm bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md text-[var(--color-text)] shadow-[0_4px_8px_var(--color-shadow)] hover:bg-[var(--color-surface-muted)]"
          >
            Reload data
          </button>
        </div>
      </div>

      <div className="flex gap-2.5 justify-between items-start flex-wrap">
        <div className="flex flex-wrap gap-2.5 justify-between items-start w-full">
          <SummaryCards dashboard={dashboard} onNotify={openNotifyModal} />

          <div className="w-full grid grid-cols-1 xl:grid-cols-5 gap-3 mb-5">
            <JobSubmissionsCard submissionBuckets={submissionBuckets} />
            <EventDurationCard eventDurationStats={sortedEventStats} />
          </div>

          <CurrentAttemptsCard
            attempts={sortedAttempts}
            totalCount={pipelineAttemptsCount}
            page={pipelineAttemptsPage}
            pageSize={pipelineAttemptsPageSize}
            onPageChange={setPipelineAttemptsPage}
            onPageSizeChange={setPipelineAttemptsPageSize}
            confirmMarkAttemptFailed={confirmMarkAttemptFailed}
          />

          <ErroredJobsCard
            erroredJobs={sortedErroredJobs}
            totalCount={erroredJobsCount}
            page={erroredJobsPage}
            pageSize={erroredJobsPageSize}
            onPageChange={setErroredJobsPage}
            onPageSizeChange={setErroredJobsPageSize}
          />

          <ProcessingJobsCard
            processingJobs={sortedProcessingJobs}
            totalCount={processingJobsCount}
            page={processingJobsPage}
            pageSize={processingJobsPageSize}
            onPageChange={setProcessingJobsPage}
            onPageSizeChange={setProcessingJobsPageSize}
            confirmMarkAttemptFailed={confirmMarkAttemptFailed}
          />

          <QueueJobsSection dashboard={dashboard} />

          {dashboard?.classifiedPipelineErrors ? (
            <UnhandledErrorsSection
              classifiedErrors={dashboard?.classifiedPipelineErrors}
              openUpdateErrorDescriptionModal={openUpdateErrorDescriptionModal}
            />
          ) : null}
        </div>
      </div>
    </main>
  );
};
